import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box, CircularProgress, Typography } from '@mui/material'
import { supabase } from '../../supabaseClient'

/**
 * Parent sign-in from an emailed link. No password.
 *
 * The old parent path met the least technical person in the family with an
 * eight-character minimum before they had seen anything, while the student in
 * the same family signs in with a code and no password at all. This gives the
 * parent the same deal: one click from the email and they are in.
 *
 * The token is single use and does not expire. Redeeming it returns a
 * magic-link hash, which is the same mechanism the student flow uses.
 */
export default function ParentSignInPage({ token, onComplete }: {
  token: string
  onComplete: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  /* StrictMode mounts twice in development. The token is single use, so a
     second redemption would burn it and fail the first. */
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    ;(async () => {
      const { data, error: invokeErr } = await supabase.functions.invoke('redeem-parent-invite', {
        body: { token },
      })
      if (invokeErr || !data?.ok || !data?.token_hash) {
        setError(data?.error || invokeErr?.message || 'We could not sign you in from this link.')
        return
      }
      const { error: vErr } = await supabase.auth.verifyOtp({
        token_hash: data.token_hash, type: 'magiclink',
      })
      if (vErr) { setError(vErr.message); return }
      window.history.replaceState(null, '', '/')
      onComplete()
    })()
  }, [token, onComplete])

  return (
    <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center', px: 2 }}>
      <Box sx={{ maxWidth: 460, textAlign: 'center' }}>
        {error ? (
          <>
            <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{error}</Alert>
            <Typography variant="body2" color="text.secondary">
              You can still get in: go to the sign-in page and enter your email, and we will
              send you a code.
            </Typography>
          </>
        ) : (
          <>
            <CircularProgress size={28} />
            <Typography variant="body1" sx={{ mt: 2 }}>Signing you in…</Typography>
          </>
        )}
      </Box>
    </Box>
  )
}
